import React from "react";
import { formatAmount } from "../../../utils/currencyFormatter";

/**
 * FarmerAdvanceBalanceCard - Displays farmer's current advance balance
 * This card shows the advance balance (debt) that the farmer needs to pay back
 * Positioned on the Farmer Payments page alongside the live balance
 */

const CARD_CLASSES = "bg-gradient-to-br from-purple-50 to-pink-100 rounded-lg border-2 border-purple-300 p-8 shadow-lg mb-6";

const FarmerAdvanceBalanceCard = ({ advanceBalance, loading }) => {
  if (loading) {
    return (
      <div className={CARD_CLASSES}>
        <div className="text-center text-gray-600">Loading advance balance...</div>
      </div>
    );
  }

  const hasAdvance = advanceBalance > 0;
  const balanceColor = hasAdvance ? "text-purple-700" : "text-gray-700";

  return (
    <div className={CARD_CLASSES}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-purple-800 flex items-center gap-2">
            <span className="inline-block w-3 h-3 bg-purple-500 rounded-full animate-pulse"></span>
            Your Advance Balance
          </h3>
          <p className="text-sm text-purple-700 mt-1">
            Real-time tracking of advance payments and deductions
          </p>
        </div>
        <div className="text-xs text-purple-600 bg-white/60 px-3 py-1 rounded-full">
          Live Data
        </div>
      </div>

      {/* Balance Display */}
      <div className="text-center py-6">
        <div className="text-5xl font-bold mb-2">
          <span className={balanceColor}>
            KES {formatAmount(advanceBalance)}
          </span>
        </div>
        <p className="text-sm text-gray-600">
          {hasAdvance 
            ? "Amount advanced that needs to be repaid" 
            : "No outstanding advance balance"}
        </p>
      </div>

      {/* Status Indicator */}
      <div className="flex items-center justify-center gap-4 mt-4">
        <div className="text-center">
          <div className="text-xs text-gray-600">Status</div>
          <div className={`text-sm font-semibold mt-1 ${hasAdvance ? 'text-purple-600' : 'text-green-600'}`}>
            {hasAdvance ? '⚠ Outstanding Advance' : '✓ No Advance'}
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="mt-6 text-xs text-purple-700 bg-white/60 p-3 rounded">
        💡 Advance payments are deducted from your milk payments. Track when advances were credited and how they've been paid off in the sections below.
      </div>
    </div>
  );
};

export default FarmerAdvanceBalanceCard;
